import { useMutation, useQuery } from '@apollo/react-hooks';
import { gql } from 'graphql-tag';
import qs from 'qs';
import { useContext } from 'react';
import { useLocation } from 'react-router-dom';
import { AppContext } from 'shared/AppContext';

// Query for fetching promos
export const GET_PROMOS_V2 = gql`
  query GetPromosV2($input: GetPromosInputV2!) {
    getPromosV2(input: $input) {
      count
      results {
        id
        name
        expiration
        no_expiration
        ticket_discount
        service_discount
        is_expired
        issuer
        limit
      }
    }
  }
`;

// Query for fetching a single promo
export const GET_PROMO = gql`
  query GetPromo($id: Int!) {
    getPromo(id: $id) {
      id
      name
      expiration
      no_expiration
      ticket_discount
      service_discount
      is_expired
      issuer
      limit
      track_id
      series_id
    }
  }
`;

// Create promo mutation
const CREATE_PROMO = gql`
  mutation CreatePromoV2($input: CreatePromoInput!) {
    createPromoV2(input: $input) {
      id
      name
      expiration
      no_expiration
      ticket_discount
      service_discount
      is_expired
      issuer
      limit
    }
  }
`;

// Update promo mutation
const UPDATE_PROMO = gql`
  mutation UpdatePromoV2($input: UpdatePromoInput!) {
    updatePromoV2(input: $input) {
      id
      name
      expiration
      no_expiration
      ticket_discount
      service_discount
      is_expired
      limit
    }
  }
`;

// Delete promo mutation
const DELETE_PROMO = gql`
  mutation DeletePromo($id: Int!) {
    deletePromo(id: $id)
  }
`;

// Hook to get promos
export function useGetPromos() {
  const {
    state: { accountId },
  } = useContext(AppContext);
  const location = useLocation();
  const search = qs.parse(location.search, { ignoreQueryPrefix: true });

  return useQuery(GET_PROMOS_V2, {
    variables: {
      input: {
        ...(search ? search : null),
        ...(accountId ? { account_id: accountId } : null),
      },
    },
    fetchPolicy: 'cache-and-network',
  });
}

// Hook to get a single promo
export function useGetPromo(promoId) {
  return useQuery(GET_PROMO, {
    variables: { id: promoId || 0 },
    skip: !promoId,
    fetchPolicy: 'cache-and-network',
  });
}

// Hook for create promo mutation
export function useCreatePromo() {
  const {
    state: { accountId },
  } = useContext(AppContext);
  const location = useLocation();
  const search = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [mutate, { loading }] = useMutation(CREATE_PROMO, {
    update: (cache, { data: { createPromoV2 } }) => {
      try {
        const existingData = cache.readQuery({
          query: GET_PROMOS_V2,
          variables: {
            input: {
              ...(Object.keys(search).length !== 0 ? { ...search } : null),
              account_id: accountId,
            },
          },
        });

        if (existingData) {
          cache.writeQuery({
            query: GET_PROMOS_V2,
            variables: {
              input: {
                ...(Object.keys(search).length !== 0 ? { ...search } : null),
                account_id: accountId,
              },
            },
            data: {
              ...existingData,
              getPromosV2: {
                ...existingData.getPromosV2,
                count: existingData.getPromosV2.count + 1,
                results: [createPromoV2, ...existingData.getPromosV2.results],
              },
            },
          });
        }
      } catch (e) {
        // Cache miss is expected if data hasn't been fetched
      }
    },
  });

  const createPromo = async (input) => {
    try {
      const response = await mutate({
        variables: { input },
      });
      return response;
    } catch (error) {
      return { errors: error };
    }
  };

  return { createPromo, loading };
}

// Hook for update promo mutation
export function useUpdatePromo() {
  const {
    state: { accountId },
  } = useContext(AppContext);
  const location = useLocation();
  const search = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [mutate, { loading }] = useMutation(UPDATE_PROMO, {
    update: (cache, { data: { updatePromoV2 } }) => {
      try {
        const existingData = cache.readQuery({
          query: GET_PROMOS_V2,
          variables: {
            input: {
              ...(Object.keys(search).length !== 0 ? { ...search } : null),
              account_id: accountId,
            },
          },
        });

        if (existingData) {
          cache.writeQuery({
            query: GET_PROMOS_V2,
            variables: {
              input: {
                ...(Object.keys(search).length !== 0 ? { ...search } : null),
                account_id: accountId,
              },
            },
            data: {
              ...existingData,
              getPromosV2: {
                ...existingData.getPromosV2,
                results: existingData.getPromosV2.results.map((promo) =>
                  parseInt(promo.id) === parseInt(updatePromoV2.id)
                    ? updatePromoV2
                    : promo
                ),
              },
            },
          });
        }
      } catch (e) {
        // Cache miss is expected if data hasn't been fetched
      }
    },
  });

  const updatePromo = async (input) => {
    try {
      const response = await mutate({
        variables: { input },
      });
      return response;
    } catch (error) {
      return { errors: error };
    }
  };

  return { updatePromo, loading };
}

// Hook for delete promo mutation
export function useDeletePromo() {
  const {
    state: { accountId },
  } = useContext(AppContext);
  const location = useLocation();
  const search = qs.parse(location.search, { ignoreQueryPrefix: true });

  const [mutate, { loading }] = useMutation(DELETE_PROMO, {
    update: (cache, { data: { deletePromo } }, { variables }) => {
      if (!deletePromo) return;

      try {
        const existingData = cache.readQuery({
          query: GET_PROMOS_V2,
          variables: {
            input: {
              ...(Object.keys(search).length !== 0 ? { ...search } : null),
              account_id: accountId,
            },
          },
        });

        if (existingData) {
          cache.writeQuery({
            query: GET_PROMOS_V2,
            variables: {
              input: {
                ...(Object.keys(search).length !== 0 ? { ...search } : null),
                account_id: accountId,
              },
            },
            data: {
              ...existingData,
              getPromosV2: {
                ...existingData.getPromosV2,
                count: existingData.getPromosV2.count - 1,
                results: existingData.getPromosV2.results.filter(
                  (promo) => promo.id !== variables.id
                ),
              },
            },
          });
        }
      } catch (e) {
        // Cache miss is expected if data hasn't been fetched
      }
    },
  });

  const deletePromo = async (id) => {
    try {
      const response = await mutate({
        variables: { id },
      });
      return response;
    } catch (error) {
      return { errors: error };
    }
  };

  return { deletePromo, loading };
}
